import React, { type PropsWithChildren } from 'react';
import { ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import Animated, { FadeIn, FadeOut, SlideInLeft, SlideOutLeft } from 'react-native-reanimated';

import { theme } from '@garnish/constants';

import { useWebKeyListener } from '../../../../../../../../hooks';
import { FocusScope } from '../../../../../../../FocusScope';
import { Icon } from '../../../../../../../Icon';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarFloatingDrawerContainer = (props: Props) => {
  const {
    accessibilityLabel,
    backdropAccessibilityLabel,
    children,
    closeAccessibilityLabel,
    onClose,
  } = props;

  // ─── Effects ───────────────────────────────────────────────────────────────

  useWebKeyListener('Escape', onClose);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <>
      <AnimatedTouchableOpacity
        entering={BACKDROP_ENTERING_ANIMATION}
        exiting={BACKDROP_EXITING_ANIMATION}
        accessibilityLabel={backdropAccessibilityLabel}
        accessibilityRole="button"
        activeOpacity={0.8}
        style={styles.backdrop}
        onPress={onClose}
      />

      <FocusScope>
        <Animated.View
          accessibilityLabel={accessibilityLabel}
          accessibilityViewIsModal={true}
          accessible={true}
          aria-label={accessibilityLabel}
          aria-modal={true}
          role="dialog"
          tabIndex={-1}
          entering={DRAWER_ENTERING_ANIMATION}
          exiting={DRAWER_EXITING_ANIMATION}
          style={styles.drawer}
        >
          <View style={styles.surface}>
            <View style={styles.header}>
              <Icon
                name="IconSweetgreenFull"
                width={DRAWER_LOGO_SIZE.width}
                height={DRAWER_LOGO_SIZE.height}
                color={theme.colors.KALE}
              />

              <TouchableOpacity
                activeOpacity={0.7}
                accessibilityLabel={closeAccessibilityLabel}
                accessibilityRole="button"
                hitSlop={theme.spacing['2']}
                onPress={onClose}
                style={styles.closeButton}
              >
                <Icon name="IconClose" color={theme.colors.CHARCOAL} width={20} height={20} />
              </TouchableOpacity>
            </View>

            <ScrollView contentContainerStyle={styles.scrollContent} style={styles.scrollView}>
              {children}
            </ScrollView>
          </View>
        </Animated.View>
      </FocusScope>
    </>
  );
};

// ─── Animated Components ─────────────────────────────────────────────────────

const AnimatedTouchableOpacity = Animated.createAnimatedComponent(TouchableOpacity);

// ─── Constants ───────────────────────────────────────────────────────────────

const DRAWER_LOGO_SIZE = { width: 126, height: 24 };

const BACKDROP_ENTERING_ANIMATION = FadeIn.duration(theme.transitions.base);
const BACKDROP_EXITING_ANIMATION = FadeOut.duration(theme.transitions.base);
const DRAWER_ENTERING_ANIMATION = SlideInLeft.duration(theme.transitions.base);
const DRAWER_EXITING_ANIMATION = SlideOutLeft.duration(theme.transitions.base);

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  backdrop: {
    ...StyleSheet.absoluteFill,
    zIndex: theme.zIndex.modalBackdrop,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
  },
  drawer: {
    width: 280,
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    zIndex: theme.zIndex.offCanvas,
  },
  surface: {
    flex: 1,
    boxShadow: '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
    backgroundColor: theme.colors.WHITE,
  },
  header: {
    zIndex: 1,
    minHeight: 68,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(14, 21, 14, 0.05)',
    paddingHorizontal: theme.spacing['5'],
  },
  closeButton: {
    width: 36,
    height: 36,
    borderRadius: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  scrollView: {
    zIndex: 1,
    flex: 1,
    minHeight: 0,
  },
  scrollContent: {
    flexGrow: 1,
    gap: theme.spacing['6'],
    paddingTop: theme.spacing['6'],
    paddingHorizontal: theme.spacing['4'],
    paddingBottom: theme.spacing['8'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  accessibilityLabel: string;
  backdropAccessibilityLabel: string;
  closeAccessibilityLabel: string;
  onClose: () => void;
}>;
